import React from "react";
import { motion } from "framer-motion";
import styles from "./HomeIntroHero.module.css";
import type {
  HomeServiceItemData,
  IHomeServicesSectionProps,
} from "@model/Home.model";
import HomeSolid from "Utils/Assets/SVGs/HomeSolid";
import CodeArrows from "Utils/Assets/SVGs/CodeArrows";
import Lab from "Utils/Assets/SVGs/SystemIntegrationIcon";
import { useMotion } from "../../../Logic/Functions/Hooks/useMotion";

const renderServiceIcon = (kind: HomeServiceItemData["iconKind"]) => {
  switch (kind) {
    case "home":
      return <HomeSolid className={styles.HSIcon} />;
    case "code":
      return <CodeArrows className={styles.HSIcon} />;
    case "power":
      return <Lab className={styles.HSIcon} />;
    default:
      return null;
  }
};

const HomeServicesSection: React.FC<IHomeServicesSectionProps> = ({
  services,
  once = false,
  triggerAmount = 0.2,
}) => {
  const { slideFade, containerStagger } = useMotion();

  if (!services.length) return null;

  return (
    <section
      className={styles.HSSection}
      aria-labelledby="home-services-title"
      data-testid="home-services-section"
    >
      <motion.header
        className={styles.HSHeader}
        variants={containerStagger(0.12)}
        initial="hidden"
        whileInView="show"
        viewport={{ once, amount: triggerAmount }}
      >
        <motion.h2
          id="home-services-title"
          className={styles.HSTitle}
          variants={slideFade("left", 0)}
        >
          Services
        </motion.h2>

        <motion.p
          className={styles.HSSubtitle}
          variants={slideFade("right", 0.06)}
        >
          We combine hardware, software, and automation expertise to build
          reliable, secure, and scalable solutions.
        </motion.p>
      </motion.header>

      <motion.div
        className={styles.HSGrid}
        variants={containerStagger(0.15)}
        initial="hidden"
        whileInView="show"
        viewport={{ once, amount: triggerAmount }}
      >
        {services.map((service, i) => (
          <motion.article
            key={service.id}
            className={styles.HSCard}
            variants={slideFade("up", 0.03 * i)}
          >
            <div className={styles.HSIconRow}>
              <span className={styles.HSIconBadge}>
                {renderServiceIcon(service.iconKind)}
              </span>
              <span className={styles.HSCategoryPill}>
                {service.categoryLabel}
              </span>
            </div>

            <h3 className={styles.HSCardTitle}>{service.title}</h3>
            <p className={styles.HSCardDescription}>{service.description}</p>

            {/* <button
              type="button"
              className={styles.HSCardCta}
              onClick={() => {
                if (service.ctaHref) {
                  window.location.href = service.ctaHref;
                }
              }}
            >
              Learn more
            </button> */}
          </motion.article>
        ))}
      </motion.div>
    </section>
  );
};

export default HomeServicesSection;
